import { useState, useEffect, useLayoutEffect, useRef, type CSSProperties } from 'react';
import { Input, Modal } from 'antd';
import { DeleteOutlined, DesktopOutlined } from '@ant-design/icons';
import {
  ApiError,
  deleteAgent,
  fetchRemoteNodeStatus,
  fetchRemoteNodeHostedAgents,
  deleteRemoteNode,
  rotateRemoteNodeConnectionToken,
  fetchRepositoryMappings,
  bindRepositoryMapping,
  verifyRepositoryMapping,
  resolveRepositoryMapping,
  removeRepositoryMapping,
  type RemoteNodeHostedAgent,
  type RemoteNodeStatus,
  type RepositoryMapping,
} from '../lib/api';
import type { RemoteNode } from '../lib/api';
import { getBorgeeWsBaseUrl } from '../lib/runtime-base-url';
import PresenceDot from './PresenceDot';
import { RemoteTree } from './RemoteTree';
import { copyToClipboard } from '../lib/clipboard';
import { AGENT_PROVIDER_LABELS } from './AgentConnectResult';
import { DecommissionModalSurface } from './DecommissionAgentModal';
import { useAgentStore } from '../stores';

const STATUS_RETRY_DELAY_MS = 1500;
const STATUS_RETRY_LIMIT = 10;
const buildInstallCommand = (server: string, token: string) =>
  `npx @borgee/remote-helper install --server ${server} --token ${token} --dirs '/path/to/dir'`;
const CHECKLIST_DONE_BADGE: CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: 'var(--color-success, #22c55e)',
  color: '#fff',
  fontSize: 11,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 1,
};
const CHECKLIST_ACTIVE_BADGE: CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '2px solid var(--accent-primary, #5865f2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 1,
  fontSize: 11,
  color: 'var(--accent-primary, #5865f2)',
};
const CHECKLIST_FUTURE_BADGE: CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '2px solid var(--text-tertiary, #ccc)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 1,
  fontSize: 11,
  color: 'var(--text-tertiary, #999)',
};

function repositoryMappingErrorMessage(
  error: unknown,
  options: {
    action: 'bind' | 'verify' | 'resolve' | 'remove';
    repository: string;
    status: RemoteNodeStatus | null;
  },
) {
  const { action, repository, status } = options;
  if (!(error instanceof Error)) {
    return action === 'bind'
      ? 'Could not bind this repository.'
      : `Could not ${action} this repository mapping.`;
  }
  if (!(error instanceof ApiError)) {
    return error.message;
  }
  switch (error.code) {
    case 'invalid_repository':
      return 'Repository must identify a github.com owner/repository.';
    case 'path_not_absolute':
      return 'Use an absolute path on the selected computer, then retry.';
    case 'path_not_allowed':
      if (status?.exposed_dirs && status.exposed_dirs.length > 0) {
        return `The directory must be inside one of this computer's exposed directories: ${status.exposed_dirs.join(', ')}. Restart Remote Helper with --dirs if the checkout lives elsewhere.`;
      }
      return "The directory is outside this computer's exposed directories. Restart Remote Helper with --dirs to expose it, then retry.";
    case 'directory_not_found':
      return 'The directory was not found on the selected computer. Check the path and retry.';
    case 'not_git_repository':
      return 'The directory exists, but it is not a Git repository.';
    case 'repository_remote_mismatch':
      return `The directory is a Git repository, but none of its remotes match ${repository}. Run git remote -v in that checkout and retry.`;
    case 'repository_mapping_not_found':
      return 'This computer does not have a saved mapping for that repository. Bind the checkout again, then retry.';
    case 'repository_mapping_unsupported':
      return 'This connected helper does not support repository mappings. Update Remote Helper, then reconnect.';
    case 'repository_mapping_failed':
      return 'Remote Helper could not complete the repository mapping request. Check the helper logs, then retry.';
    default:
      return error.message;
  }
}

interface Props {
  nodeId: string;
  node: RemoteNode;
  token?: string;
  onDeleted: () => void;
  onTokenRotated?: (token: string) => void;
}

export default function ComputerDetailView({
  nodeId,
  node,
  token,
  onDeleted,
  onTokenRotated,
}: Props) {
  // antd's static Modal.confirm renders outside the React tree, so it never
  // reaches our ConfigProvider and falls back to antd's default palette. The
  // hook form renders through the context holder below and picks up the theme.
  const [modal, modalContextHolder] = Modal.useModal();
  const [status, setStatus] = useState<RemoteNodeStatus | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedExposedDir, setSelectedExposedDir] = useState<string | null>(null);
  const [rotatedToken, setRotatedToken] = useState<string | null>(null);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [hostedAgents, setHostedAgents] = useState<RemoteNodeHostedAgent[]>([]);
  const [hostedAgentsLoading, setHostedAgentsLoading] = useState(true);
  const [hostedAgentsError, setHostedAgentsError] = useState<string | null>(null);
  const [deletingHostedAgentId, setDeletingHostedAgentId] = useState<string | null>(null);
  const [deleteChecklistOpen, setDeleteChecklistOpen] = useState(false);
  const [deleteImpactAcknowledged, setDeleteImpactAcknowledged] = useState(false);
  const [deleteHostedAcknowledged, setDeleteHostedAcknowledged] = useState(false);
  const [deletingComputer, setDeletingComputer] = useState(false);
  const [repositoryMappings, setRepositoryMappings] = useState<RepositoryMapping[]>([]);
  const [repositoryMappingsLoading, setRepositoryMappingsLoading] = useState(false);
  const [repositoryMappingError, setRepositoryMappingError] = useState<string | null>(null);
  const [repositoryInput, setRepositoryInput] = useState('');
  const [repositoryPathInput, setRepositoryPathInput] = useState('');
  const [repositoryMappingAction, setRepositoryMappingAction] = useState<string | null>(null);
  const [repositoryMappingResult, setRepositoryMappingResult] = useState<string | null>(null);
  const currentNodeIdRef = useRef(nodeId);
  const repositoryMappingLoadGenerationRef = useRef(0);
  const loadAgents = useAgentStore((s) => s.loadAgents);

  useLayoutEffect(() => {
    currentNodeIdRef.current = nodeId;
    repositoryMappingLoadGenerationRef.current += 1;
  }, [nodeId]);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;
    let attempts = 0;

    setStatus(null);

    const loadStatus = () => {
      fetchRemoteNodeStatus(nodeId)
        .then((nextStatus) => {
          if (cancelled) return;
          setStatus(nextStatus);
          if (
            (nextStatus.online && nextStatus.exposed_dirs !== null) ||
            attempts >= STATUS_RETRY_LIMIT
          ) {
            return;
          }
          attempts += 1;
          timeoutId = window.setTimeout(loadStatus, STATUS_RETRY_DELAY_MS);
        })
        .catch(() => {
          if (cancelled) return;
          setStatus({ online: false, exposed_dirs: null });
          if (attempts >= STATUS_RETRY_LIMIT) {
            return;
          }
          attempts += 1;
          timeoutId = window.setTimeout(loadStatus, STATUS_RETRY_DELAY_MS);
        });
    };

    loadStatus();

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [nodeId]);

  const repositoryMappingSupported =
    status?.online === true && status.capabilities?.includes('repository_mapping') === true;

  const refreshRepositoryMappings = async () => {
    const requestNodeId = nodeId;
    const requestGeneration = ++repositoryMappingLoadGenerationRef.current;
    setRepositoryMappingsLoading(true);
    setRepositoryMappingError(null);
    try {
      const mappings = await fetchRepositoryMappings(requestNodeId);
      if (
        currentNodeIdRef.current !== requestNodeId ||
        repositoryMappingLoadGenerationRef.current !== requestGeneration
      ) {
        return;
      }
      setRepositoryMappings(mappings);
    } catch (error) {
      if (
        currentNodeIdRef.current !== requestNodeId ||
        repositoryMappingLoadGenerationRef.current !== requestGeneration
      ) {
        return;
      }
      setRepositoryMappingError(
        error instanceof Error ? error.message : 'Could not load repository mappings.',
      );
    } finally {
      if (
        currentNodeIdRef.current === requestNodeId &&
        repositoryMappingLoadGenerationRef.current === requestGeneration
      ) {
        setRepositoryMappingsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (repositoryMappingSupported) {
      void refreshRepositoryMappings();
    } else {
      repositoryMappingLoadGenerationRef.current += 1;
      setRepositoryMappings([]);
      setRepositoryMappingsLoading(false);
      setRepositoryMappingError(null);
    }
  }, [nodeId, repositoryMappingSupported]);

  const handleBindRepository = async () => {
    if (!repositoryInput.trim() || !repositoryPathInput.trim() || repositoryMappingAction) return;
    const requestNodeId = nodeId;
    setRepositoryMappingAction('bind');
    setRepositoryMappingError(null);
    setRepositoryMappingResult(null);
    try {
      const mapping = await bindRepositoryMapping(
        requestNodeId,
        repositoryInput.trim(),
        repositoryPathInput.trim(),
      );
      if (currentNodeIdRef.current !== requestNodeId) return;
      setRepositoryInput(mapping.repository);
      setRepositoryPathInput(mapping.path);
      setRepositoryMappingResult(`Bound ${mapping.repository} to ${mapping.path}.`);
      await refreshRepositoryMappings();
    } catch (error) {
      if (currentNodeIdRef.current !== requestNodeId) return;
      setRepositoryMappingError(
        repositoryMappingErrorMessage(error, {
          action: 'bind',
          repository: repositoryInput.trim(),
          status,
        }),
      );
    } finally {
      if (currentNodeIdRef.current === requestNodeId) {
        setRepositoryMappingAction(null);
      }
    }
  };

  const runRepositoryMappingAction = async (
    action: 'verify' | 'resolve' | 'remove',
    mapping: RepositoryMapping,
  ) => {
    if (repositoryMappingAction) return;
    const requestNodeId = nodeId;
    setRepositoryMappingAction(`${action}:${mapping.repository}`);
    setRepositoryMappingError(null);
    setRepositoryMappingResult(null);
    try {
      if (action === 'remove') {
        await removeRepositoryMapping(requestNodeId, mapping.repository);
        if (currentNodeIdRef.current !== requestNodeId) return;
        setRepositoryMappingResult(`Removed ${mapping.repository}. Local files were not deleted.`);
        await refreshRepositoryMappings();
      } else {
        const checked =
          action === 'verify'
            ? await verifyRepositoryMapping(requestNodeId, mapping.repository)
            : await resolveRepositoryMapping(requestNodeId, mapping.repository);
        if (currentNodeIdRef.current !== requestNodeId) return;
        setRepositoryMappingResult(
          action === 'verify'
            ? `Verified ${checked.repository} at ${checked.path}.`
            : `Resolved ${checked.repository} to ${checked.path}.`,
        );
      }
    } catch (error) {
      if (currentNodeIdRef.current !== requestNodeId) return;
      setRepositoryMappingError(
        repositoryMappingErrorMessage(error, {
          action,
          repository: mapping.repository,
          status,
        }),
      );
    } finally {
      if (currentNodeIdRef.current === requestNodeId) {
        setRepositoryMappingAction(null);
      }
    }
  };

  useEffect(() => {
    setShowToken(false);
    setCopied(false);
    setSelectedExposedDir(null);
    setRotatedToken(null);
    setRotateError(null);
    setDeleteError(null);
    setDeleteChecklistOpen(false);
    setDeleteImpactAcknowledged(false);
    setDeleteHostedAcknowledged(false);
    setDeletingComputer(false);
    setRepositoryMappings([]);
    setRepositoryMappingsLoading(false);
    setRepositoryMappingError(null);
    setRepositoryInput('');
    setRepositoryPathInput('');
    setRepositoryMappingAction(null);
    setRepositoryMappingResult(null);
  }, [nodeId]);

  useEffect(() => {
    let cancelled = false;
    setHostedAgentsLoading(true);
    setHostedAgentsError(null);
    fetchRemoteNodeHostedAgents(nodeId)
      .then((agents) => {
        if (cancelled) return;
        setHostedAgents(agents);
        setHostedAgentsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setHostedAgentsLoading(false);
        setHostedAgentsError(err instanceof Error ? err.message : 'Could not load hosted agents.');
      });
    return () => {
      cancelled = true;
    };
  }, [nodeId]);

  const refreshHostedAgents = async () => {
    setHostedAgentsLoading(true);
    setHostedAgentsError(null);
    try {
      setHostedAgents(await fetchRemoteNodeHostedAgents(nodeId));
    } catch (err) {
      setHostedAgentsError(err instanceof Error ? err.message : 'Could not load hosted agents.');
    } finally {
      setHostedAgentsLoading(false);
    }
  };

  const closeDeleteChecklist = () => {
    if (deletingComputer) return;
    setDeleteChecklistOpen(false);
    setDeleteImpactAcknowledged(false);
    setDeleteHostedAcknowledged(false);
  };

  const handleDelete = () => {
    setDeleteError(null);
    setDeleteChecklistOpen(true);
    setDeleteImpactAcknowledged(false);
    setDeleteHostedAcknowledged(false);
  };

  const handleDeleteComputerConfirmed = async () => {
    setDeleteError(null);
    setDeletingComputer(true);
    try {
      await deleteRemoteNode(nodeId);
      await loadAgents();
      setDeleteChecklistOpen(false);
      onDeleted();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Could not delete this computer.');
      setDeletingComputer(false);
    }
  };

  const handleRegenerateToken = () => {
    modal.confirm({
      title: 'Regenerate Connection Token',
      content: `The current connection token for "${node.machine_name}" stops working immediately, so this computer goes offline right away. It stays offline until you re-run the new install command on it.`,
      okText: 'Regenerate Token',
      okButtonProps: { danger: true },
      onOk: async () => {
        setRotateError(null);
        let nextToken: string;
        try {
          nextToken = await rotateRemoteNodeConnectionToken(nodeId);
        } catch (err) {
          setRotateError(
            err instanceof Error ? err.message : 'Could not regenerate the connection token.',
          );
          return;
        }
        setRotatedToken(nextToken);
        // The plaintext token never comes back from any read, so reveal it
        // straight away rather than hiding it behind another click.
        setShowToken(true);
        onTokenRotated?.(nextToken);
      },
    });
  };

  const agentWsServer = getBorgeeWsBaseUrl();
  const currentToken = rotatedToken ?? token;
  // A command built around anything but the real token cannot enroll the
  // machine, so there is no install command to offer until one is in memory.
  const installCommand = currentToken
    ? {
        displayed: buildInstallCommand(agentWsServer, showToken ? currentToken : '••••••••'),
        copyable: buildInstallCommand(agentWsServer, currentToken),
      }
    : null;

  const handleCopy = (command: string) => {
    copyToClipboard(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDeleteHostedAgent = (agent: RemoteNodeHostedAgent) => {
    modal.confirm({
      title: 'Delete Hosted Agent',
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ margin: 0 }}>
            Delete hosted agent <b>{agent.display_name}</b>?
          </p>
          <p style={{ margin: 0 }}>
            This will permanently delete the agent and stop <b>{node.machine_name}</b> from hosting
            it on the next remote-helper sync.
          </p>
          <p style={{ margin: 0 }}>
            This will not delete this computer or affect any other hosted agents on it.
          </p>
        </div>
      ),
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        setHostedAgentsError(null);
        setDeletingHostedAgentId(agent.id);
        try {
          await deleteAgent(agent.id);
          loadAgents();
          await refreshHostedAgents();
        } catch (err) {
          setHostedAgentsError(
            err instanceof Error ? err.message : 'Could not delete this hosted agent.',
          );
        } finally {
          setDeletingHostedAgentId(null);
        }
      },
    });
  };

  return (
    <div className="agent-detail-view">
      {modalContextHolder}
      {deleteChecklistOpen && (
        <DeleteComputerChecklistModal
          machineName={node.machine_name}
          hostedAgents={hostedAgents}
          hostedAgentsLoading={hostedAgentsLoading}
          hostedAgentsError={hostedAgentsError}
          deleteError={deleteError}
          impactAcknowledged={deleteImpactAcknowledged}
          hostedAcknowledged={deleteHostedAcknowledged}
          deleting={deletingComputer}
          onAcknowledgeImpact={() => setDeleteImpactAcknowledged(true)}
          onAcknowledgeHosted={() => setDeleteHostedAcknowledged(true)}
          onClose={closeDeleteChecklist}
          onDelete={handleDeleteComputerConfirmed}
        />
      )}
      <div className="agent-detail-header">
        <DesktopOutlined style={{ fontSize: 20, color: 'var(--text-secondary)' }} />
        <div className="agent-detail-header-text">
          <div className="agent-detail-header-name">
            {node.machine_name}
            {status !== null && (
              <PresenceDot
                state={status.online ? 'online' : 'offline'}
                reason={undefined}
                compact
              />
            )}
          </div>
        </div>
      </div>

      <div className="agent-detail-content">
        <div className="agent-settings-scroll">
          <div className="agent-page">
            <div className="agent-profile-hero">
              <span
                className="agent-profile-avatar"
                style={{ background: 'var(--text-secondary)' }}
              >
                <DesktopOutlined style={{ fontSize: 24 }} />
              </span>
              <div className="agent-profile-info">
                <div className="agent-profile-name">
                  {node.machine_name}
                  {status !== null && (
                    <>
                      <PresenceDot
                        state={status.online ? 'online' : 'offline'}
                        reason={undefined}
                        compact
                      />
                      <span className="agent-profile-status">
                        {status.online ? 'Online' : 'Offline'}
                      </span>
                    </>
                  )}
                </div>
                <div className="agent-profile-slug">ID: {node.id}</div>
              </div>
            </div>


            <div className="agent-profile-field">
              <div className="agent-profile-field-label">MACHINE NAME</div>
              <div className="agent-profile-field-value">{node.machine_name}</div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">CREATED</div>
              <div className="agent-profile-field-value">
                {new Date(node.created_at).toLocaleDateString()}
              </div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">LAST SEEN</div>
              <div className="agent-profile-field-value agent-profile-field-dim">
                {node.last_seen_at ? new Date(node.last_seen_at).toLocaleString() : 'Never'}
              </div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">CONNECTION</div>
              <div className="agent-profile-field-value" style={{ display: 'grid', gap: 12 }}>
                <div>
                  <div
                    className="node-token-area"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                  >
                    {currentToken && (
                      <button className="btn btn-sm" onClick={() => setShowToken(!showToken)}>
                        {showToken ? 'Hide Token' : 'Show Token'}
                      </button>
                    )}
                    <button className="btn btn-sm" onClick={handleRegenerateToken}>
                      Regenerate Token
                    </button>
                    {currentToken && showToken && (
                      <code className="node-token">{currentToken}</code>
                    )}
                  </div>
                  {!currentToken && (
                    <p
                      className="node-token-unavailable agent-profile-field-dim"
                      style={{ margin: '8px 0 0 0' }}
                    >
                      A connection token can be read only at the moment it is issued, so there is no
                      install command to show for this computer. Regenerate the token to get a
                      working one — the current token stops authenticating immediately, and this
                      computer stays offline until you run the new install command on it.
                    </p>
                  )}
                  {rotateError && (
                    <p className="node-token-error remote-error" style={{ margin: '8px 0 0 0' }}>
                      {rotateError}
                    </p>
                  )}
                </div>
                {installCommand && (
                  <div>
                    <div className="agent-profile-field-label" style={{ marginBottom: 6 }}>
                      INSTALL COMMAND
                    </div>
                    <div
                      className="node-cmd-box"
                      style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <code>{installCommand.displayed}</code>
                      <button
                        className="btn btn-sm"
                        onClick={() => handleCopy(installCommand.copyable)}
                      >
                        {copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <p className="agent-profile-field-dim" style={{ margin: '8px 0 0 0' }}>
                      Run this on the target computer (macOS or Linux). Replace the quoted{' '}
                      <code>'/path/to/dir'</code> value with at least one directory to expose.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">EXPOSED DIRECTORIES</div>
              <div className="agent-profile-field-value" style={{ display: 'grid', gap: 8 }}>
                {status === null ? (
                  <div className="agent-profile-field-dim">Loading...</div>
                ) : status.online && status.exposed_dirs && status.exposed_dirs.length > 0 ? (
                  <>
                    {selectedExposedDir ? (
                      <div className="remote-panel" style={{ marginTop: 4 }}>
                        <div className="remote-panel-header">
                          <button
                            className="remote-back-btn"
                            onClick={() => setSelectedExposedDir(null)}
                          >
                            ← Back
                          </button>
                          <span className="remote-panel-title">{selectedExposedDir}</span>
                        </div>
                        <RemoteTree nodeId={nodeId} rootPath={selectedExposedDir} />
                      </div>
                    ) : (
                      <div className="remote-binding-list">
                        {status.exposed_dirs.map((dir) => (
                          <button
                            key={dir}
                            type="button"
                            className="remote-binding-item"
                            onClick={() => setSelectedExposedDir(dir)}
                            style={{ width: '100%', textAlign: 'left', background: 'none' }}
                          >
                            <span className="remote-binding-icon">🖥️</span>
                            <div className="remote-binding-info">
                              <div className="remote-binding-name">{dir}</div>
                              <div className="remote-binding-meta">Browse this computer root</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : status.online ? (
                  <div className="agent-profile-field-dim">
                    Connected, but this computer has not announced any exposed directories yet.
                  </div>
                ) : (
                  <div className="agent-profile-field-dim">
                    Exposed directories are available only while this computer is online.
                  </div>
                )}
              </div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">REPOSITORY MAPPINGS</div>
              <div className="agent-profile-field-value" style={{ display: 'grid', gap: 10 }}>
                {status === null ? (
                  <div className="agent-profile-field-dim">Loading...</div>
                ) : !status.online ? (
                  <div className="agent-profile-field-dim">
                    Start Remote Helper on this computer to manage repository mappings.
                  </div>
                ) : !repositoryMappingSupported ? (
                  <div className="agent-profile-field-dim">
                    This connected helper does not support repository mappings. Update Remote
                    Helper, then reconnect.
                  </div>
                ) : (
                  <>
                    <div className="agent-profile-field-dim">
                      Bind an existing local Git checkout. Borgee validates its remotes but never
                      clones, pulls, runs Git, or deletes local code.
                    </div>
                    <Input
                      aria-label="Repository identity"
                      value={repositoryInput}
                      onChange={(event) => setRepositoryInput(event.target.value)}
                      placeholder="github.com/owner/repository"
                    />
                    <Input
                      aria-label="Existing repository directory"
                      value={repositoryPathInput}
                      onChange={(event) => setRepositoryPathInput(event.target.value)}
                      placeholder="/absolute/path/to/existing/checkout"
                    />
                    {status.exposed_dirs && status.exposed_dirs.length > 0 && (
                      <div className="agent-profile-field-dim">
                        The checkout must live under one of this computer&apos;s exposed
                        directories: {status.exposed_dirs.join(', ')}.
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={
                        !repositoryInput.trim() ||
                        !repositoryPathInput.trim() ||
                        repositoryMappingAction !== null
                      }
                      onClick={() => void handleBindRepository()}
                    >
                      {repositoryMappingAction === 'bind'
                        ? 'Binding...'
                        : 'Add Repository Mapping'}
                    </button>
                    {repositoryMappingsLoading ? (
                      <div className="agent-profile-field-dim">Loading mappings...</div>
                    ) : repositoryMappings.length === 0 ? (
                      <div className="agent-profile-field-dim">No repository mappings yet.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: 8 }}>
                        {repositoryMappings.map((mapping) => (
                          <div
                            key={mapping.repository}
                            style={{
                              border: '1px solid var(--border-color, #e5e7eb)',
                              borderRadius: 8,
                              padding: 10,
                              display: 'grid',
                              gap: 6,
                            }}
                          >
                            <code>{mapping.repository}</code>
                            <span className="agent-profile-field-dim">{mapping.path}</span>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                className="btn btn-sm"
                                disabled={repositoryMappingAction !== null}
                                onClick={() => void runRepositoryMappingAction('verify', mapping)}
                              >
                                Verify
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm"
                                disabled={repositoryMappingAction !== null}
                                onClick={() => void runRepositoryMappingAction('resolve', mapping)}
                              >
                                Resolve
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm"
                                disabled={repositoryMappingAction !== null}
                                onClick={() => void runRepositoryMappingAction('remove', mapping)}
                              >
                                Remove Mapping
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {repositoryMappingResult && (
                      <div
                        role="status"
                        style={{ color: 'var(--color-success, #22c55e)', fontSize: 12 }}
                      >
                        {repositoryMappingResult}
                      </div>
                    )}
                    {repositoryMappingError && (
                      <div role="alert" className="remote-error">
                        {repositoryMappingError}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">HOSTED AGENTS ({hostedAgents.length})</div>
              <div className="agent-profile-field-value" style={{ display: 'grid', gap: 10 }}>
                <div className="agent-profile-field-dim">
                  This computer currently hosts {hostedAgents.length} agent
                  {hostedAgents.length === 1 ? '' : 's'}.
                </div>

                {hostedAgentsLoading ? (
                  <div className="agent-profile-field-dim">Loading...</div>
                ) : hostedAgentsError ? (
                  <p className="remote-error" style={{ margin: 0 }}>
                    {hostedAgentsError}
                  </p>
                ) : hostedAgents.length === 0 ? (
                  <div className="agent-profile-field-dim">No hosted agents yet.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {hostedAgents.map((agent) => (
                      <div
                        key={agent.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 12,
                          alignItems: 'center',
                          padding: 12,
                          border: '1px solid var(--border-color, #e5e7eb)',
                          borderRadius: 8,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>{agent.display_name}</div>
                          <div className="agent-profile-field-dim">
                            {AGENT_PROVIDER_LABELS[agent.provider]}
                            {agent.enabled ? ' · Enabled' : ' · Stopped'}
                            {agent.disabled ? ' · Agent disabled' : ''}
                          </div>
                          <div
                            className="agent-profile-field-dim"
                            style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}
                          >
                            Agent ID: {agent.id}
                          </div>
                        </div>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteHostedAgent(agent)}
                          disabled={deletingHostedAgentId === agent.id}
                        >
                          {deletingHostedAgentId === agent.id ? 'Deleting...' : 'Delete Agent'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="agent-profile-field">
              <div className="agent-profile-field-label">ACTIONS</div>
              <div style={{ marginTop: 4 }}>
                <button className="btn btn-sm btn-danger" onClick={handleDelete}>
                  <DeleteOutlined /> Delete Computer
                </button>
                {deleteError && (
                  <p className="node-delete-error remote-error" style={{ margin: '8px 0 0 0' }}>
                    {deleteError}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeleteComputerChecklistModal({
  machineName,
  hostedAgents,
  hostedAgentsLoading,
  hostedAgentsError,
  deleteError,
  impactAcknowledged,
  hostedAcknowledged,
  deleting,
  onAcknowledgeImpact,
  onAcknowledgeHosted,
  onClose,
  onDelete,
}: {
  machineName: string;
  hostedAgents: RemoteNodeHostedAgent[];
  hostedAgentsLoading: boolean;
  hostedAgentsError: string | null;
  deleteError: string | null;
  impactAcknowledged: boolean;
  hostedAcknowledged: boolean;
  deleting: boolean;
  onAcknowledgeImpact: () => void;
  onAcknowledgeHosted: () => void;
  onClose: () => void;
  onDelete: () => void;
}) {
  const readyToDelete = impactAcknowledged && hostedAcknowledged;

  return (
    <DecommissionModalSurface maxWidth={520} busy={deleting} onClose={onClose}>
      <h3>Delete {machineName}</h3>
      <p style={{ fontSize: 13, margin: '16px 0', color: 'var(--text-secondary)' }}>
        This checklist confirms what will be removed before this computer is deleted for good.
      </p>
      {deleteError && (
        <p className="node-delete-error remote-error" style={{ margin: '0 0 12px 0' }}>
          {deleteError}
        </p>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          marginTop: 16,
          marginBottom: 8,
        }}
      >
        {impactAcknowledged ? (
          <>
            <span style={CHECKLIST_DONE_BADGE}>&#10003;</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}>
              Connection cleanup acknowledged
            </span>
          </>
        ) : (
          <>
            <span style={CHECKLIST_ACTIVE_BADGE}>1</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Review connection impact</span>
            <button
              className="btn btn-sm btn-primary"
              style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 10px' }}
              onClick={onAcknowledgeImpact}
            >
              Acknowledge
            </button>
          </>
        )}
      </div>
      {!impactAcknowledged && (
        <div style={{ marginLeft: 28 }}>
          <p style={{ fontSize: 12, marginBottom: 0, color: 'var(--text-secondary)' }}>
            Deleting this computer removes its connection token, drops its bindings, and disconnects
            it from Borgee immediately.
          </p>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          marginTop: 12,
          marginBottom: 8,
        }}
      >
        {hostedAcknowledged ? (
          <>
            <span style={CHECKLIST_DONE_BADGE}>&#10003;</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}>
              Hosted-agent cascade acknowledged
            </span>
          </>
        ) : impactAcknowledged ? (
          <>
            <span style={CHECKLIST_ACTIVE_BADGE}>2</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Review hosted-agent impact</span>
            <button
              className="btn btn-sm btn-primary"
              style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 10px' }}
              onClick={onAcknowledgeHosted}
            >
              Acknowledge
            </button>
          </>
        ) : (
          <>
            <span style={CHECKLIST_FUTURE_BADGE}>2</span>
            <span style={{ fontSize: 13, color: 'var(--text-tertiary, #999)' }}>
              Review hosted-agent impact
            </span>
          </>
        )}
      </div>
      {impactAcknowledged && !hostedAcknowledged && (
        <div style={{ marginLeft: 28 }}>
          <p style={{ fontSize: 12, marginBottom: 8, color: 'var(--text-secondary)' }}>
            Every hosted agent on this computer will be deleted too.
          </p>
          {hostedAgentsLoading ? (
            <p className="agent-profile-field-dim" style={{ margin: 0 }}>
              Loading hosted agents...
            </p>
          ) : hostedAgentsError ? (
            <p className="remote-error" style={{ margin: 0 }}>
              {hostedAgentsError} Hosted agents, if any, are still deleted together with this
              computer.
            </p>
          ) : hostedAgents.length === 0 ? (
            <p className="agent-profile-field-dim" style={{ margin: 0 }}>
              No hosted agents are currently bound to this computer.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: 4, marginBottom: 8 }}>
              {hostedAgents.map((agent) => (
                <div key={agent.id} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  <b>{agent.display_name}</b> · {AGENT_PROVIDER_LABELS[agent.provider]} · {agent.id}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          marginTop: 12,
          marginBottom: 16,
        }}
      >
        {readyToDelete ? (
          <>
            <span style={CHECKLIST_ACTIVE_BADGE}>3</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Delete permanently</span>
          </>
        ) : (
          <>
            <span style={CHECKLIST_FUTURE_BADGE}>3</span>
            <span style={{ fontSize: 13, color: 'var(--text-tertiary, #999)' }}>
              Delete permanently
            </span>
          </>
        )}
      </div>

      <p style={{ fontSize: 12, marginTop: 0, marginBottom: 16, color: 'var(--text-secondary)' }}>
        This action cannot be undone.
      </p>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn btn-sm" onClick={onClose} disabled={deleting}>
          Cancel
        </button>
        <button
          className="btn btn-sm"
          style={{
            background: 'var(--text-danger, #d32f2f)',
            color: '#fff',
            borderColor: 'var(--text-danger, #d32f2f)',
            opacity: readyToDelete ? 1 : 0.65,
          }}
          onClick={onDelete}
          disabled={!readyToDelete || deleting}
        >
          {deleting ? 'Deleting...' : 'Delete Computer'}
        </button>
      </div>
    </DecommissionModalSurface>
  );
}
